import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { useClient, useClientTags, useCopyDocs, useCopyMutations } from '@/api/queries'
import { PageHeader } from '@/components/app/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable } from '@/components/ui/DataTable'
import { Input, Select } from '@/components/ui/Field'
import { ConfirmDialog } from '@/components/ui/Overlay'
import { EmptyState } from '@/components/ui/States'
import { useToast } from '@/components/ui/Toast'
import { CopyDrawer } from './CopyDrawer'
import { buildColumns } from './columns'
import { COPY_TYPES } from '@/lib/constants'
import type { CopyDoc } from '@/types'

/**
 * Saved, versioned copy artifacts. Not a review surface — no approve/reject,
 * so no `useReviewCollection`.
 *
 * Two write paths by design (docs/00-overview.md §5.3): Claude saves here
 * over MCP, and a copywriter can paste by hand. The manual path is what keeps
 * the page useful before the `save_copy` tool ships.
 */
export function CopyPage() {
  const { clientId } = useParams()
  const { data: client } = useClient(clientId!)
  const { data: docs = [], isLoading } = useCopyDocs(clientId!)
  const { data: tagPool = [] } = useClientTags(clientId!)
  const { save, favorite, remove } = useCopyMutations(clientId!)
  const toast = useToast()

  const [query, setQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [open, setOpen] = useState<CopyDoc | 'new' | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<CopyDoc | null>(null)

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return docs.filter((d) => {
      if (favoritesOnly && !d.favorite) return false
      if (typeFilter !== 'all' && d.copyType !== typeFilter) return false
      if (needle && !`${d.name} ${d.body}`.toLowerCase().includes(needle)) return false
      return true
    })
  }, [docs, query, typeFilter, favoritesOnly])

  const columns = buildColumns({
    onFavorite: (id, next) => favorite.mutate({ id, favorite: next }),
  })

  return (
    <>
      <PageHeader
        title="Copy library"
        description="Finished copy for this client. Claude saves here over MCP, and you can add anything by hand."
        actions={
          <Button variant="primary" onClick={() => setOpen('new')}>
            <Plus size={14} aria-hidden />
            New copy
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3">
          <div className="relative">
            <Search
              size={13}
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search copy"
              aria-label="Search copy"
              className="h-8 w-56 pl-7 text-[13px]"
            />
          </div>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filter by type"
            className="h-8 w-40 text-[13px]"
          >
            <option value="all">All types</option>
            {COPY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            variant={favoritesOnly ? 'primary' : 'secondary'}
            aria-pressed={favoritesOnly}
            onClick={() => setFavoritesOnly((v) => !v)}
          >
            Favorites
          </Button>
        </div>

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(d) => d.id}
          loading={isLoading}
          onRowClick={(d) => setOpen(d)}
          defaultSort={{ key: 'updated', dir: 'desc' }}
          empty={
            <EmptyState
              title={docs.length === 0 ? 'No saved copy yet' : 'Nothing matches these filters'}
              description={
                docs.length === 0
                  ? 'Copy arrives here two ways: Claude saves it over the MCP connection while you work, or you add it by hand.'
                  : undefined
              }
              action={
                <Button size="sm" variant="primary" onClick={() => setOpen('new')}>
                  New copy
                </Button>
              }
            />
          }
        />
      </Card>

      <CopyDrawer
        doc={open}
        clientName={client?.name ?? ''}
        tagPool={tagPool}
        onClose={() => setOpen(null)}
        onSave={async (patch) => {
          await save.mutateAsync(patch)
          setOpen(null)
          toast.push(patch.id ? 'Copy saved as a new version.' : 'Copy added.')
        }}
        onFavorite={(id, next) => favorite.mutate({ id, favorite: next })}
        onDelete={(doc) => setConfirmDelete(doc)}
        onToast={(msg) => toast.push(msg)}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return
          await remove.mutateAsync(confirmDelete.id)
          setConfirmDelete(null)
          setOpen(null)
          toast.push('Copy deleted.')
        }}
        title={`Delete “${confirmDelete?.name ?? ''}”?`}
        description="Every version of this copy is removed. There is no undo."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
      />
    </>
  )
}
